import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAppNavigation } from 'src/routes';
import { useAuth } from '../contexts/AuthContext';
import { useAuth0 } from '@auth0/auth0-react';
import SuspenseLoader from './SuspenseLoader';
import { useGetConfig, useGetConfigAuth } from 'src/hooks/api/useConfigEndpoint';
import { useClient } from 'src/api/axios/config';
import { isNativeMobileAuthAvailable } from 'src/utils/nativeAuth';

interface ProtectedRoutesProps {
  requiredPermissions?: string[];
  children?: React.ReactNode;
}

export const ProtectedRoutes: React.FC<ProtectedRoutesProps> = ({ requiredPermissions, children }) => {
  const location = useLocation();
  const { permissions, isAuthenticated, currentUser, isAuthenticating } = useAuth();
  const { isLoading, isAuthenticated: auth0IsAuthenticated } = useAuth0();
  const { routesMap } = useAppNavigation();
  const { data: configAuthData, isLoading: isConfigLoading } = useGetConfigAuth();
  const isNativeWrapper = isNativeMobileAuthAvailable();
  const client = useClient();
  useGetConfig();

  if (isLoading || isAuthenticating || isConfigLoading) {
    return <SuspenseLoader />;
  }

  if (isAuthenticated) {
    const pathname = location.pathname;
    const adminPath = pathname.startsWith('/admin-clients') || pathname.startsWith('/admin-users');
    if (client === 'support') {
      if (!adminPath) {
        return <Navigate to={routesMap.adminClients} replace state={{ from: location }} />;
      }
    } else {
      if (adminPath) {
        return <Navigate to={routesMap.landing} replace state={{ from: location }} />;
      }
    }

    if (requiredPermissions && !currentUser?.sysAdmin) {
      if (!requiredPermissions.some((permission) => permissions?.includes(permission))) {
        return <Navigate to={routesMap.noPermission} replace state={{ from: location }} />;
      }
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (!isAuthenticated && isNativeWrapper && configAuthData?.platform === 'Auth0') {
    return <SuspenseLoader />;
  }

  if (!auth0IsAuthenticated && configAuthData?.platform === 'Auth0') {
    return <Navigate to={routesMap.auth0Callback} replace state={{ from: location }} />;
  }

  if (!isAuthenticated && configAuthData && configAuthData.platform !== 'Auth0') {
    return <Navigate to={routesMap.login} replace state={{ from: location }} />;
  }
};
